import { useCallback, useMemo, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import { Table } from 'antd';
import type { ColumnsType, TableProps } from 'antd/es/table';

import { usePagination } from '@/shared/hooks/usePagination';
import { DEFAULT_PAGE_SIZE } from '@/shared/lib/constants';
import { ColumnVisibilityButton } from '@/shared/ui/ColumnVisibilityButton/ColumnVisibilityButton';
import { useColumnVisibility } from '@/shared/ui/ColumnVisibilityButton/useColumnVisibility';
import { SearchInput } from '@/shared/ui/SearchInput';
import { Switch } from '@/shared/ui/Switch';
import { TableEmpty } from '@/shared/ui/TableEmpty';

import styles from './DataTable.module.css';

export type DataTableProps<TData, TColumnKey extends string> = {
  data: TData[];
  columns: Record<string, ColumnsType<TData>[number]>;
  defaultColumnOrder: TColumnKey[];
  defaultVisibleColumns: TColumnKey[];
  searchPlaceholder: string;
  searchFilter: (item: TData, query: string) => boolean;
  isLoading?: boolean;
  includeDeleted?: boolean;
  onDeletedToggle?: (includeDeleted: boolean) => void;
  rowKey: (record: TData) => string;
  className?: string;
  emptyDescription?: string;
  viewportOffset?: number;
  tableProps?: Omit<TableProps<TData>, 'columns' | 'dataSource' | 'rowKey' | 'loading' | 'className' | 'locale'>;
};

export const DataTable = <TData, TColumnKey extends string>({
  data,
  columns,
  defaultColumnOrder,
  defaultVisibleColumns,
  searchPlaceholder,
  searchFilter,
  isLoading = false,
  includeDeleted = false,
  onDeletedToggle,
  rowKey,
  className,
  emptyDescription = '데이터가 없습니다.',
  viewportOffset = 648,
  tableProps,
}: DataTableProps<TData, TColumnKey>) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState<string>('');

  const allColumnsMap = useMemo(
    () =>
      Object.entries(columns).reduce(
        (acc, [key, col]) => ({
          ...acc,
          [key]: { title: col.title as string },
        }),
        {} as Record<TColumnKey, { title: string }>
      ),
    [columns]
  );

  const { state } = useColumnVisibility<TColumnKey>({
    defaultColumnOrder,
    defaultVisibleColumns,
    allColumnsMap,
  });
  const { columnOrder, visibleColumns } = state;

  const visibleColumnsData = useMemo<ColumnsType<TData>>(() => {
    return columnOrder.filter((key) => visibleColumns.includes(key)).map((key) => columns[key as TColumnKey]);
  }, [columnOrder, columns, visibleColumns]);

  const filteredData = useMemo(() => {
    if (visibleColumnsData.length === 0) return [];
    if (!searchQuery || searchQuery.length === 0) return data;

    const lowerQuery = searchQuery.toLowerCase();
    return data.filter((item) => searchFilter(item, lowerQuery));
  }, [searchQuery, data, visibleColumnsData, searchFilter]);

  const pageParam = searchParams.get('page');
  const pageSizeParam = searchParams.get('pageSize');
  const initialPage = pageParam ? parseInt(pageParam, 10) : 1;
  const initialPageSize = pageSizeParam ? parseInt(pageSizeParam, 10) : DEFAULT_PAGE_SIZE;

  const handlePageChange = useCallback(
    (page: number, pageSize: number) => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('page', String(page));
      newSearchParams.set('pageSize', String(pageSize));
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams]
  );

  const { paginationConfig, scrollConfig } = usePagination({
    dataLength: filteredData.length,
    viewportOffset,
    initialPage,
    initialPageSize,
    onPageChange: handlePageChange,
  });

  return (
    <div className={styles.dataTable}>
      <div className={styles.toolbar}>
        <SearchInput
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div className={styles.toolbarRight}>
          {onDeletedToggle && (
            <div className={styles.deletedToggle}>
              <Switch checked={includeDeleted} onChange={onDeletedToggle} aria-label="삭제됨 포함" color="standard" />
              <span className={styles.toggleLabel}>삭제됨</span>
            </div>
          )}
          <ColumnVisibilityButton
            defaultColumnOrder={columnOrder}
            defaultVisibleColumns={visibleColumns}
            allColumnsMap={allColumnsMap}
          />
        </div>
      </div>
      <Table
        columns={visibleColumnsData}
        dataSource={filteredData}
        rowKey={rowKey}
        loading={isLoading}
        pagination={paginationConfig}
        scroll={{ x: filteredData.length > 0 ? 'max-content' : undefined, ...scrollConfig }}
        locale={{
          emptyText: (
            <TableEmpty
              searchQuery={searchQuery}
              isVisibleColumns={visibleColumnsData.length === 0}
              description={emptyDescription}
            />
          ),
        }}
        className={className}
        {...tableProps}
      />
    </div>
  );
};
